import axios, { AxiosInstance, AxiosResponse } from 'axios';
import config from '../config';
import {
  AuthTokens,
  LoginCredentials,
  ChangePasswordData,
  CreateTenantData,
  UpdateTenantData,
  Tenant,
  ApiResponse,
  LoginResponse,
  SignatureLookupResponse,
  UserLogResponse,
  UsageMetricsResponse
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = config.apiBaseUrl;
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};

        // If it's a change-password call, don't try to refresh; let the caller handle 401
        const url: string = (originalRequest.url as string) || '';
        if (url.includes('/auth/change-password')) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            const refreshToken = localStorage.getItem('refreshToken');
            const email = localStorage.getItem('userEmail');
            const tenantId = localStorage.getItem('tenantId');
            
            if (refreshToken && email) {
              const response = await this.refreshToken(refreshToken, email, tenantId || undefined);
              localStorage.setItem('accessToken', response.accessToken);
              localStorage.setItem('refreshToken', response.refreshToken);
              
              // Retry the original request
              originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            this.clearAuth();
            window.location.href = '/login';
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response: AxiosResponse<LoginResponse> = await this.api.post('/auth/login-via-email', credentials);
    return response.data;
  }

  async refreshToken(refreshToken: string, email: string, tenantId?: string): Promise<AuthTokens> {
    const response: AxiosResponse<AuthTokens> = await this.api.post('/auth/refresh', {
      refreshToken,
      email,
      tenantId
    });
    return response.data;
  }

  async changePassword(data: ChangePasswordData): Promise<ApiResponse> {
    // Prefer persisted user object (_id), then fallback to userId key
    let userId = '';
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const parsed = JSON.parse(userJson);
        if (parsed && parsed._id) {
          userId = parsed._id;
        }
      }
    } catch (e) {
      console.error('Error parsing user from localStorage:', e);
    }
    if (!userId) {
      userId = localStorage.getItem('userId') || '';
    }
    if (!userId) {
      throw new Error('User ID not found');
    }

    const response: AxiosResponse<ApiResponse> = await this.api.post('/auth/change-password', {
      userId,
      currentPassword: data.currentPassword,
      newPassword: data.newPassword
    });
    return response.data;
  }

  async validateToken(token: string): Promise<{ valid: boolean; expiresAt: number }> {
    const response: AxiosResponse<{ valid: boolean; expiresAt: number }> = await this.api.post('/auth/validate', { token });
    return response.data;
  }

  // Tenant endpoints
  async getTenants(): Promise<Tenant[]> {
    const response: AxiosResponse<Tenant[]> = await this.api.get('/tenants');
    return response.data;
  }

  async getTenant(id: string): Promise<Tenant> {
    const response: AxiosResponse<Tenant> = await this.api.get(`/tenants/${id}`);
    return response.data;
  }

  async createTenant(data: CreateTenantData): Promise<Tenant> {
    const response: AxiosResponse<Tenant> = await this.api.post('/tenants', data);
    return response.data;
  }

  async updateTenant(id: string, data: UpdateTenantData): Promise<Tenant> {
    const response: AxiosResponse<Tenant> = await this.api.put(`/tenants/${id}`, data);
    return response.data;
  }

  async deleteTenant(id: string): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/tenants/${id}`);
    return response.data;
  }

  // User endpoints
  async getUsers(): Promise<Array<{ _id: string; email: string; createdAt?: string; updatedAt?: string }>> {
    const response: AxiosResponse<any[]> = await this.api.get('/users');
    return response.data;
  }

  async createUser(email: string, password: string): Promise<{ _id: string; email: string; createdAt?: string; updatedAt?: string }> {
    const response: AxiosResponse<any> = await this.api.post('/users', { email, password });
    return response.data;
  }

  async updateUserEmail(id: string, email: string): Promise<{ _id: string; email: string; createdAt?: string; updatedAt?: string }> {
    const response: AxiosResponse<any> = await this.api.put(`/users/${id}`, { email });
    return response.data;
  }

  async deleteUser(id: string): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/users/${id}`);
    return response.data;
  }

  async getSignaturePreview(email: string, language: 'en' | 'he', options?: { skipCache?: boolean }): Promise<SignatureLookupResponse> {
    const response: AxiosResponse<SignatureLookupResponse> = await this.api.get('/signature', {
      params: {
        email,
        language,
        inline: false,
        skipCache: options?.skipCache === true
      }
    });
    return response.data;
  }

  async getUserLogs(email: string, source: 'server' | 'smtp', options?: { limit?: number }): Promise<UserLogResponse> {
    const response: AxiosResponse<UserLogResponse> = await this.api.get('/logs/user-activity', {
      params: {
        email,
        source,
        limit: options?.limit
      }
    });
    return response.data;
  }

  async getUsageMetrics(domain?: string): Promise<UsageMetricsResponse> {
    const response: AxiosResponse<UsageMetricsResponse> = await this.api.get('/logs/usage-metrics', {
      params: domain ? { domain } : undefined
    });
    return response.data;
  }

  // Utility methods
  clearAuth(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userId');
    localStorage.removeItem('tenantId');
  }

  setAuth(tokens: AuthTokens, user: { email: string; id: string; tenantId?: string }): void {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    localStorage.setItem('userEmail', user.email);
    localStorage.setItem('userId', user.id);
    try {
      localStorage.setItem('user', JSON.stringify({ _id: user.id, email: user.email, tenantId: user.tenantId }));
    } catch {}
    if (user.tenantId) {
      localStorage.setItem('tenantId', user.tenantId);
    }
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('accessToken');
  }
}

const apiService = new ApiService();
export default apiService;
